import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  Tab,
  Tabs,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import { useQuery, type UseMutationResult } from "@tanstack/react-query";
import {
  masterclassesApi,
  type Masterclass,
  type UpsertMasterclassPayload,
} from "../api/masterclassesApi";

interface ClientOption {
  id: string;
  name: string;
}

interface MasterclassesTabProps {
  createMasterclassMutation: UseMutationResult<
    unknown,
    unknown,
    UpsertMasterclassPayload
  >;
  updateMasterclassMutation: UseMutationResult<
    unknown,
    unknown,
    { id: string; payload: Partial<UpsertMasterclassPayload> }
  >;
  deleteMasterclassMutation: UseMutationResult<unknown, unknown, string>;
  masterclassesLoading: boolean;
  masterclassesList: Masterclass[];
  clientsList: ClientOption[];
}

interface FormState {
  title: string;
  description: string;
  instructor: string;
  location: string;
  imageUrl: string;
  startsAt: string;
  endsAt: string;
  capacity: string;
  isActive: boolean;
  clientIds: string[];
}

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  instructor: "",
  location: "",
  imageUrl: "",
  startsAt: "",
  endsAt: "",
  capacity: "",
  isActive: true,
  clientIds: [],
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time.
const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value: string): string | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const formatWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const MasterclassesTab: React.FC<MasterclassesTabProps> = ({
  createMasterclassMutation,
  updateMasterclassMutation,
  deleteMasterclassMutation,
  masterclassesLoading,
  masterclassesList,
  clientsList,
}) => {
  const [view, setView] = useState<"list" | "calendar">("list");
  const [calendarMonth, setCalendarMonth] = useState<Dayjs>(() =>
    dayjs().startOf("month"),
  );
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Masterclass whose attendee list is open in the bookings dialog.
  const [viewingBookings, setViewingBookings] = useState<Masterclass | null>(
    null,
  );
  const bookingsQuery = useQuery({
    queryKey: ["admin", "masterclass-bookings", viewingBookings?.id],
    queryFn: () => masterclassesApi.listBookings(viewingBookings!.id),
    enabled: !!viewingBookings,
  });
  const bookings = bookingsQuery.data?.data ?? [];

  const bookingName = (b: { first_name: string | null; last_name: string | null; email: string | null }) =>
    [b.first_name, b.last_name].filter(Boolean).join(" ").trim() ||
    b.email ||
    "Unknown user";

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    clientsList.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [clientsList]);

  const filtered = useMemo(
    () =>
      masterclassesList.filter((m) =>
        `${m.title} ${m.instructor ?? ""}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    [masterclassesList, search],
  );

  // Masterclasses grouped by calendar day (YYYY-MM-DD) for the calendar view.
  const byDay = useMemo(() => {
    const map = new Map<string, Masterclass[]>();
    masterclassesList.forEach((m) => {
      if (!m.starts_at) return;
      const key = dayjs(m.starts_at).format("YYYY-MM-DD");
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    });
    return map;
  }, [masterclassesList]);

  // 6-week grid (Mon-first) covering the visible month.
  const calendarDays = useMemo(() => {
    const firstOfMonth = calendarMonth.startOf("month");
    // dayjs day(): 0=Sun..6=Sat. Shift so the grid starts on Monday.
    const offset = (firstOfMonth.day() + 6) % 7;
    const gridStart = firstOfMonth.subtract(offset, "day");
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  }, [calendarMonth]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Optional prefill: a "YYYY-MM-DDTHH:mm" local datetime (used by the calendar
  // double-click-to-add flow).
  const openCreate = (prefillStartsAt?: string) => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, startsAt: prefillStartsAt ?? "" });
    setUploadError(null);
    setDialogOpen(true);
  };

  const openEdit = (m: Masterclass) => {
    setEditingId(m.id);
    setUploadError(null);
    setForm({
      title: m.title,
      description: m.description ?? "",
      instructor: m.instructor ?? "",
      location: m.location ?? "",
      imageUrl: m.image_url ?? "",
      startsAt: toLocalInput(m.starts_at),
      endsAt: toLocalInput(m.ends_at),
      capacity: m.capacity != null ? String(m.capacity) : "",
      isActive: m.is_active,
      clientIds: m.client_ids,
    });
    setDialogOpen(true);
  };

  const buildPayload = (): UpsertMasterclassPayload | null => {
    const startsIso = fromLocalInput(form.startsAt);
    if (!form.title.trim() || !startsIso) return null;
    const capacityNum = form.capacity.trim() ? Number(form.capacity) : null;
    return {
      title: form.title.trim(),
      description: form.description.trim() || null,
      instructor: form.instructor.trim() || null,
      location: form.location.trim() || null,
      image_url: form.imageUrl.trim() || null,
      starts_at: startsIso,
      ends_at: fromLocalInput(form.endsAt),
      capacity:
        capacityNum != null && Number.isFinite(capacityNum)
          ? Math.max(0, Math.trunc(capacityNum))
          : null,
      is_active: form.isActive,
      client_ids: form.clientIds,
    };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { upload_url, file_url } = await masterclassesApi.createUploadUrl({
        filename: file.name,
        content_type: file.type || "application/octet-stream",
      });
      await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      set("imageUrl", file_url);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isSaving =
    createMasterclassMutation.isPending || updateMasterclassMutation.isPending;

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) return;
    if (editingId) {
      updateMasterclassMutation.mutate(
        { id: editingId, payload },
        { onSuccess: () => setDialogOpen(false) },
      );
    } else {
      createMasterclassMutation.mutate(payload, {
        onSuccess: () => setDialogOpen(false),
      });
    }
  };

  return (
    <SettingsSection>
      <Alert severity="info">
        Masterclasses are only bookable by the clients you assign them to. A
        masterclass with no clients selected is visible to nobody.
      </Alert>

      <SectionCard
        title="Masterclasses"
        description={`${masterclassesList.length} masterclass${masterclassesList.length === 1 ? "" : "es"} configured`}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => openCreate()}
          >
            Add masterclass
          </Button>
        }
      >
        <Tabs
          value={view}
          onChange={(_, v) => setView(v)}
          sx={{ mb: 2, minHeight: 0 }}
        >
          <Tab label="List" value="list" sx={{ minHeight: 0, py: 1 }} />
          <Tab label="Calendar" value="calendar" sx={{ minHeight: 0, py: 1 }} />
        </Tabs>

        {view === "calendar" ? (
          <MasterclassCalendar
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            byDay={byDay}
            onSelectMasterclass={setViewingBookings}
            onAddOnDay={(day) =>
              // Default new masterclass to 09:00 on the double-clicked day.
              openCreate(day.hour(9).minute(0).format("YYYY-MM-DDTHH:mm"))
            }
            calendarDays={calendarDays}
          />
        ) : (
        <>
        <TextField
          size="small"
          fullWidth
          placeholder="Search masterclasses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {masterclassesLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading masterclasses…
            </Typography>
          </Stack>
        ) : masterclassesList.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <EventOutlinedIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No masterclasses yet.</Typography>
            <Typography variant="caption">
              Click "Add masterclass" to create one.
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No masterclasses match "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((m) => {
              const noClients = m.client_ids.length === 0;
              return (
                <Box
                  key={m.id}
                  onClick={() => setViewingBookings(m)}
                  sx={{
                    py: 1.5,
                    px: 1,
                    mx: -1,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Avatar
                      variant="rounded"
                      src={m.image_url || undefined}
                      sx={{ width: 64, height: 44, bgcolor: "action.hover", flexShrink: 0 }}
                    >
                      <EventOutlinedIcon fontSize="small" sx={{ opacity: 0.4 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>
                          {m.title}
                        </Typography>
                        <Chip
                          label={m.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={m.is_active ? "success" : "default"}
                          variant="outlined"
                        />
                        {m.capacity != null && (
                          <Chip
                            label={`${m.booking_count}/${m.capacity} booked`}
                            size="small"
                            variant="outlined"
                            color={m.booking_count >= m.capacity ? "warning" : "default"}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                        {formatWhen(m.starts_at)}
                        {m.instructor ? ` · ${m.instructor}` : ""}
                        {m.location ? ` · ${m.location}` : ""}
                      </Typography>
                      <Box sx={{ mt: 0.75, display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                        {noClients ? (
                          <Chip
                            icon={<WarningAmberIcon />}
                            label="No clients — not bookable"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          m.client_ids.map((cid) => (
                            <Chip
                              key={cid}
                              label={clientNameById.get(cid) ?? cid}
                              size="small"
                            />
                          ))
                        )}
                      </Box>
                    </Box>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(m);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleteMasterclassMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              "Delete this masterclass? Bookings will be removed. This cannot be undone.",
                            )
                          ) {
                            deleteMasterclassMutation.mutate(m.id);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
        </>
        )}
      </SectionCard>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit masterclass" : "Add masterclass"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              autoFocus
              required
            />
            <TextField
              size="small"
              fullWidth
              label="Description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Image (shown in the mobile carousel)
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={form.imageUrl || undefined}
                  sx={{ width: 96, height: 64, bgcolor: "action.hover" }}
                >
                  <EventOutlinedIcon sx={{ opacity: 0.4 }} />
                </Avatar>
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={
                      uploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />
                    }
                  >
                    {uploading ? "Uploading…" : "Upload image"}
                  </Button>
                  {form.imageUrl && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => set("imageUrl", "")}
                      sx={{ ml: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Stack>
              {uploadError && (
                <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                  {uploadError}
                </Alert>
              )}
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                label="Instructor"
                value={form.instructor}
                onChange={(e) => set("instructor", e.target.value)}
              />
              <TextField
                size="small"
                fullWidth
                label="Location"
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                fullWidth
                label="Starts at"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => set("startsAt", e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                size="small"
                fullWidth
                label="Ends at"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => set("endsAt", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              size="small"
              label="Capacity (leave blank for unlimited)"
              type="number"
              value={form.capacity}
              onChange={(e) => set("capacity", e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ maxWidth: 280 }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="masterclass-clients-label">
                Bookable by clients
              </InputLabel>
              <Select
                labelId="masterclass-clients-label"
                multiple
                value={form.clientIds}
                onChange={(e) =>
                  set(
                    "clientIds",
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : e.target.value,
                  )
                }
                input={<OutlinedInput label="Bookable by clients" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "None"
                    : selected
                        .map((id) => clientNameById.get(id) ?? id)
                        .join(", ")
                }
              >
                {clientsList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={form.clientIds.includes(c.id)} />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.title.trim() || !form.startsAt || isSaving || uploading}
            onClick={handleSave}
          >
            {isSaving ? (
              <CircularProgress size={18} />
            ) : editingId ? (
              "Save changes"
            ) : (
              "Add masterclass"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewingBookings}
        onClose={() => setViewingBookings(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {viewingBookings?.title ?? "Masterclass"}
          <IconButton
            onClick={() => setViewingBookings(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewingBookings && (
            <Stack spacing={2}>
              {/* ── Details ─────────────────────────────────────────────── */}
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Avatar
                  variant="rounded"
                  src={viewingBookings.image_url || undefined}
                  sx={{ width: 96, height: 64, bgcolor: "action.hover", flexShrink: 0 }}
                >
                  <EventOutlinedIcon sx={{ opacity: 0.4 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                    <Chip
                      label={viewingBookings.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={viewingBookings.is_active ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      label={
                        viewingBookings.capacity != null
                          ? `${bookings.length}/${viewingBookings.capacity} booked`
                          : `${bookings.length} booked`
                      }
                      size="small"
                      variant="outlined"
                      color={
                        viewingBookings.capacity != null &&
                        bookings.length >= viewingBookings.capacity
                          ? "warning"
                          : "default"
                      }
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatWhen(viewingBookings.starts_at)}
                    {viewingBookings.ends_at ? ` – ${formatWhen(viewingBookings.ends_at)}` : ""}
                  </Typography>
                  {(viewingBookings.instructor || viewingBookings.location) && (
                    <Typography variant="body2" color="text.secondary">
                      {[viewingBookings.instructor, viewingBookings.location]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {viewingBookings.description && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {viewingBookings.description}
                </Typography>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Bookable by
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {viewingBookings.client_ids.length === 0 ? (
                    <Chip
                      icon={<WarningAmberIcon />}
                      label="No clients — not bookable"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ) : (
                    viewingBookings.client_ids.map((cid) => (
                      <Chip key={cid} label={clientNameById.get(cid) ?? cid} size="small" />
                    ))
                  )}
                </Box>
              </Box>

              <Divider />

              {/* ── Attendees ───────────────────────────────────────────── */}
              <Typography variant="subtitle2">
                Attendees ({bookings.length})
              </Typography>
              {bookingsQuery.isLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading attendees…
                  </Typography>
                </Stack>
              ) : bookingsQuery.isError ? (
                <Alert severity="error">Failed to load attendees. Please try again.</Alert>
              ) : bookings.length === 0 ? (
                <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
                  <PeopleOutlineIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">No bookings yet.</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {bookings.map((b) => (
                    <ListItem key={b.id} disableGutters divider>
                      <ListItemAvatar>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {bookingName(b).charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={bookingName(b)}
                        secondary={
                          [b.email, `Booked ${formatWhen(b.created_at)}`]
                            .filter(Boolean)
                            .join(" · ")
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            disabled={deleteMasterclassMutation.isPending}
            onClick={() => {
              if (!viewingBookings) return;
              if (
                window.confirm(
                  "Delete this masterclass? Bookings will be removed. This cannot be undone.",
                )
              ) {
                deleteMasterclassMutation.mutate(viewingBookings.id, {
                  onSuccess: () => setViewingBookings(null),
                });
              }
            }}
          >
            Delete
          </Button>
          <Box>
            <Button onClick={() => setViewingBookings(null)}>Close</Button>
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              sx={{ ml: 1 }}
              onClick={() => {
                const m = viewingBookings;
                if (!m) return;
                setViewingBookings(null);
                openEdit(m);
              }}
            >
              Edit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface MasterclassCalendarProps {
  month: Dayjs;
  onMonthChange: (next: Dayjs) => void;
  byDay: Map<string, Masterclass[]>;
  calendarDays: Dayjs[];
  onSelectMasterclass: (m: Masterclass) => void;
  /** Double-click an empty part of a day cell to start a new masterclass. */
  onAddOnDay: (day: Dayjs) => void;
}

const MasterclassCalendar: React.FC<MasterclassCalendarProps> = ({
  month,
  onMonthChange,
  byDay,
  calendarDays,
  onSelectMasterclass,
  onAddOnDay,
}) => {
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => onMonthChange(month.subtract(1, "month"))}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ minWidth: 140, textAlign: "center" }}>
            {month.format("MMMM YYYY")}
          </Typography>
          <IconButton size="small" onClick={() => onMonthChange(month.add(1, "month"))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => onMonthChange(dayjs().startOf("month"))}>
            Today
          </Button>
          <Typography variant="caption" color="text.secondary">
            Double-click a day to add
          </Typography>
        </Stack>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {WEEKDAYS.map((d) => (
          <Typography
            key={d}
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", fontWeight: 600, py: 0.5 }}
          >
            {d}
          </Typography>
        ))}
        {calendarDays.map((day) => {
          const key = day.format("YYYY-MM-DD");
          const items = byDay.get(key) ?? [];
          const inMonth = day.month() === month.month();
          const isToday = key === today;
          return (
            <Box
              key={key}
              onDoubleClick={() => onAddOnDay(day)}
              sx={{
                minHeight: 92,
                p: 0.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: isToday ? "primary.main" : "divider",
                bgcolor: inMonth ? "background.paper" : "action.hover",
                opacity: inMonth ? 1 : 0.55,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
                "&:hover": { borderColor: "primary.light" },
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: isToday ? 700 : 500, color: isToday ? "primary.main" : "text.secondary" }}
              >
                {day.date()}
              </Typography>
              {items.map((m) => (
                <Tooltip
                  key={m.id}
                  title={`${m.title} · ${dayjs(m.starts_at).format("HH:mm")}${m.capacity != null ? ` · ${m.booking_count}/${m.capacity} booked` : ""}`}
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectMasterclass(m);
                    }}
                    sx={{
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      bgcolor: m.is_active ? "primary.main" : "action.disabledBackground",
                      color: m.is_active ? "primary.contrastText" : "text.secondary",
                      fontSize: 11,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                    }}
                  >
                    {dayjs(m.starts_at).format("HH:mm")} {m.title}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
